import React, { useEffect, useState } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { UserPlus, ArrowLeft, CheckCircle, LogOut, Loader2 } from "lucide-react";
import { registerUser, validateInvitationToken } from '../services/userAuthService';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import useAuthStore from '../store/authStore';

export function RegisterPage() {
  useDocumentTitle('Register - Propel');
  const { tenantSlug } = useParams();
  const loginPath = tenantSlug ? `/${tenantSlug}/login` : '/login';
  const { isAuthenticated, hasHydrated, user, logout } = useAuthStore();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("");
  const [token, setToken] = useState("");
  const [invitationError, setInvitationError] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [accountSwitchError, setAccountSwitchError] = useState(null);
  const [emailFromUrl, setEmailFromUrl] = useState("");
  const [roleFromUrl, setRoleFromUrl] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get("token");
    
    if (!tokenParam) {
      setInvitationError("Invalid invitation link. No token provided.");
      setIsValidating(false);
      return;
    }

    setToken(tokenParam);
    
    // Validate token before showing form
    const validateToken = async () => {
      try {
        setIsValidating(true);
        const result = await validateInvitationToken(tokenParam);
        
        if (result.valid) {
          if (result.email) {
            setEmail(result.email);
            setEmailFromUrl(result.email);
          }
          if (result.role) {
            setRole(result.role);
            setRoleFromUrl(result.role);
          }
        } else {
          setInvitationError(result.error || "Invalid or expired invitation token");
        }
      } catch (err) {
        setInvitationError(err?.message || "Failed to validate invitation token");
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, []);

  // useEffect(() => {
  //   // Only redirect if not authenticated and no invite token
  //   if (hasHydrated && !isAuthenticated && !token) {
  //     navigate("/", { replace: true });
  //   }
  //   // Optionally, you can also allow superusers to access this page
  // }, [isAuthenticated, hasHydrated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await registerUser({ name, email, password, role, token });
      setSuccess("Account created successfully. Redirecting to sign in...");
      setTimeout(() => navigate(loginPath, { replace: true }), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOutAndContinue = async () => {
    setAccountSwitchError(null);
    setIsSigningOut(true);
    try {
      await logout();
    } catch (err) {
      setAccountSwitchError(err?.message || 'Failed to sign out. Please try again.');
    } finally {
      setIsSigningOut(false);
    }
  };

  if (!hasHydrated) {
    return null;
  }

  if (isValidating) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
            <p className="text-gray-600">Validating invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (invitationError) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="flex justify-center mb-8">
              <div className="bg-red-100 p-3 rounded-full">
                <UserPlus className="h-8 w-8 text-red-600" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">
              Invalid Invitation
            </h2>

            <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-md mb-6">
              {invitationError}
            </div>

            <div className="text-center">
              <Link
                to={loginPath}
                className="inline-flex items-center text-primary-600 hover:text-primary-700 text-sm font-medium transition-colors duration-300"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isAuthenticated && user) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="flex justify-center mb-6">
              <div className="bg-primary-100 p-3 rounded-full">
                <LogOut className="h-8 w-8 text-primary-600" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-center text-gray-900 mb-4">
              Switch Account to Accept Invite
            </h2>

            <p className="text-sm text-gray-600 mb-4">
              You are currently signed in as <strong>{user.email}</strong>.
            </p>
            <p className="text-sm text-gray-600 mb-6">
              To accept this invitation{email ? ` for ${email}` : ''}, sign out first, then continue registration.
            </p>

            {accountSwitchError && (
              <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-md mb-4 text-sm">
                {accountSwitchError}
              </div>
            )}

            <button
              type="button"
              onClick={handleSignOutAndContinue}
              disabled={isSigningOut}
              className="w-full flex items-center justify-center gap-2 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
            >
              {isSigningOut ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
              {isSigningOut ? 'Signing out...' : 'Sign Out and Continue'}
            </button>

            <div className="mt-6 text-center">
              <Link
                to="/dashboard"
                className="inline-flex items-center text-primary-600 hover:text-primary-700 text-sm font-medium transition-colors duration-300"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex justify-center mb-8">
            <div className="bg-primary-100 p-3 rounded-full">
              <UserPlus className="h-8 w-8 text-primary-600" />
            </div>
          </div>

          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">
            User Registration
          </h2>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded-md mb-6">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-primary-50 border border-primary-200 text-primary-600 p-3 rounded-md mb-6 flex items-center">
              <CheckCircle className="h-5 w-5 mr-2" />
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Name
              </label>
              <input
                id="name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 px-3 py-2 transition-colors duration-200"
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email address
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 px-3 py-2 transition-colors duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                readOnly={!!emailFromUrl}
              />
            </div>

            <div>
              <label
                htmlFor="role"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Role
              </label>
              <input
                id="role"
                type="text"
                required
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 px-3 py-2 transition-colors duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                readOnly={!!roleFromUrl}
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 px-3 py-2 transition-colors duration-200"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
            >
              {isLoading ? "Creating new user..." : "Create User"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to={loginPath}
              className="inline-flex items-center text-primary-600 hover:text-primary-700 text-sm font-medium transition-colors duration-300"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
